import { CheckCircle2, MessageSquareReply, MessagesSquare, Pencil, Send } from 'lucide-react';
import { useState } from 'react';
import { href } from '../../lib/router';
import { useToast } from '../../ui/Toasts';
import { useCanAnswer, useCanSubmit } from '../assessments/api';
import { useChangeRequests, useReplyToRequest, useSendBack } from './api';

/**
 * The changes an insurer asked of the business (task 61): each answer to change, or reply to, and the
 * button that sends the assessment back once every one is done. Shown while the assessment is with the
 * business for changes.
 */
export function ChangesForYou({
  id,
  status,
  compact = false,
}: {
  id: string;
  status: string;
  compact?: boolean;
}) {
  const open = status === 'in_remediation';
  const list = useChangeRequests(id, open);
  const canAnswer = useCanAnswer();
  const canSend = useCanSubmit();
  const sendBack = useSendBack(id);
  const toast = useToast();
  if (!open || !list.data) return null;
  const sent = list.data.requests.filter((r) => r.state === 'sent');
  if (!sent.length) return null;
  const left = sent.filter((r) => !r.addressed).length;

  return (
    <section className="card pad changes-card" aria-labelledby={`changes-${id}`}>
      <div className="row-between">
        <h2 id={`changes-${id}`} className="section-title">
          Your insurer asked for {sent.length === 1 ? 'a change' : `${sent.length} changes`}
        </h2>
        <span className={`pill ${left ? 'warn' : 'ok'}`}>{left ? `${left} to do` : 'All done'}</span>
      </div>
      <p className="muted small">
        Change each answer, or reply to say why it’s right as it is. Only these answers can change. Then send
        it back to your insurer.
      </p>
      {!compact || left ? (
        <ol className="changes">
          {sent.map((r) => (
            <Request key={r.id} id={id} r={r} canAnswer={canAnswer} />
          ))}
        </ol>
      ) : null}
      <div className="row gap wrap mt">
        {canAnswer && left > 0 && (
          <a className="btn" href={href.chat(id)}>
            <MessagesSquare size={15} /> Go through them in chat
          </a>
        )}
        {canSend && (
          <button
            type="button"
            className="btn btn-primary"
            disabled={!list.data.ready || sendBack.isPending}
            title={list.data.ready ? undefined : 'Change or reply to each one first'}
            onClick={async () => {
              try {
                await sendBack.mutateAsync();
                toast('Sent back to your insurer', 'ok');
              } catch (err) {
                toast(err instanceof Error ? err.message : 'Could not send it back', 'bad');
              }
            }}
          >
            <Send size={15} /> {sendBack.isPending ? 'Sending…' : 'Send back to insurer'}
          </button>
        )}
      </div>
    </section>
  );
}

function Request({
  id,
  r,
  canAnswer,
}: {
  id: string;
  r: { id: string; question_key: string; question_text: string; message: string; reply: string | null; addressed: boolean }; // prettier-ignore
  canAnswer: boolean;
}) {
  const reply = useReplyToRequest(id);
  const toast = useToast();
  const [writing, setWriting] = useState(false);
  const [text, setText] = useState('');
  return (
    <li className={r.addressed ? 'done' : ''}>
      <div className="row gap top">
        {r.addressed ? <CheckCircle2 size={16} className="ok-t" /> : <span className="dot" aria-hidden />}
        <div className="grow">
          <div className="qtext">{r.question_text}</div>
          <p className="small insurer-says">“{r.message}”</p>
          {r.reply && (
            <p className="small muted">
              <b>You replied:</b> {r.reply}
            </p>
          )}
          {canAnswer && (
            <div className="row gap wrap">
              <a className="btn btn-sm" href={href.answer(id, r.question_key)}>
                <Pencil size={13} /> Change answer
              </a>
              <button type="button" className="btn btn-sm btn-ghost" onClick={() => setWriting(!writing)}>
                <MessageSquareReply size={13} /> {r.reply ? 'Edit reply' : 'Reply instead'}
              </button>
              <a className="btn btn-sm btn-ghost" href={href.messages(id, r.question_key)}>
                <MessagesSquare size={13} /> Discuss
              </a>
            </div>
          )}
          {writing && (
            <form
              className="stack-sm mt"
              onSubmit={async (e) => {
                e.preventDefault();
                if (!text.trim()) return;
                try {
                  await reply.mutateAsync({ rid: r.id, text: text.trim() });
                  setWriting(false);
                  setText('');
                } catch (err) {
                  toast(err instanceof Error ? err.message : 'Could not send your reply', 'bad');
                }
              }}
            >
              <label className="small" htmlFor={`reply-${r.id}`}>
                Why is the answer right as it is?
              </label>
              <textarea
                id={`reply-${r.id}`}
                className="input"
                rows={2}
                maxLength={2000}
                value={text}
                autoFocus
                onChange={(e) => setText(e.target.value)}
              />
              <div className="row gap">
                <button
                  type="submit"
                  className="btn btn-sm btn-primary"
                  disabled={!text.trim() || reply.isPending}
                >
                  Save reply
                </button>
                <button type="button" className="btn btn-sm btn-ghost" onClick={() => setWriting(false)}>
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </li>
  );
}
